import { GenericSelect } from '@/components/GenericSelect';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { useUserStore } from '@/store/userStore';
import { trpc } from '@/trpcClient';
import { checkMaterialEditInput, EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { EDeviceManagerType } from '@shelflix/schemas/material/enums';
import { FacilityDto } from '@shelflix/trpc/routes/facility/list/useCase';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { useMaterialEditStore } from '../store';
import { TagsDropdown } from '@/components/TagsDropdown';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';

interface MaterialInfoSectionProps {
  form: UseFormReturn<
    EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
      selectedTags?: { uuid: string; name: string }[];
    }
  >;
  refetchCampaigns: () => void;
  thumbnail: File;
  setThumbnail: React.Dispatch<React.SetStateAction<File>>;
  code: string;
  materialToEdit?: { contractUrl?: string };
  canEdit?: boolean;
  deviceManagerType: EDeviceManagerType;
  setDeviceManagerType: (type: EDeviceManagerType) => void;
  installedAt: Date | undefined;
  setInstalledAt: (date: Date | undefined) => void;
}

export function MaterialInfoSection({
  form,
  refetchCampaigns,
  thumbnail,
  setThumbnail,
  code,
  materialToEdit,
  canEdit = false,
  deviceManagerType,
  setDeviceManagerType,
  installedAt,
  setInstalledAt,
}: MaterialInfoSectionProps) {
  const { contract, selectedFacility, selectedMaterialType, setContract, setSelectedFacility } = useMaterialEditStore();
  const canEditMaterial = canEdit;
  // FIREBASE and LED_PANEL are the same app-managed device - LED_PANEL just adds panel geometry fields.
  const isFirebaseFamily =
    deviceManagerType === EDeviceManagerType.FIREBASE || deviceManagerType === EDeviceManagerType.LED_PANEL;

  const user = useUserStore((s) => s.user);
  const isMaster = user?.isMaster ?? false;

  const [cnpjSearch, setCnpjSearch] = useState('');

  const dropzoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const queryFacility = useQuery({
    ...trpc.facility.list.queryOptions({
      search: {
        fields: ['cnpj'],
        value: cnpjSearch ?? '',
      },
    }),
    enabled: false,
  });

  return (
    <Card className="rounded-[10px]">
      <CardHeader className="pb-6 sm:pb-0">
        <CardTitle className="text-2xl leading-5 font-semibold">Informações de Material</CardTitle>
      </CardHeader>
      <CardContent className="flex w-full flex-col gap-8 sm:flex-row">
        <div>
          <Dropzone
            removable={false}
            onFilesSelected={(files) => {
              setThumbnail(files[0]);
            }}
            ref={dropzoneRef}
            title="Selecione o tipo de material para carregar a imagem"
            description=""
            variant="square"
            requiredAspectRatio="1:1"
            value={thumbnail ? [thumbnail] : undefined}
            readonly={!canEditMaterial}
          />
        </div>

        <div className="w-full">
          <div className="grid w-full grid-cols-6 gap-4 sm:flex-row sm:items-end">
            <div className="col-span-2 flex w-full flex-col gap-1">
              <span className="text-xs font-medium text-[var(--gray-primary)]">Tipo de material</span>
              <Input disabled value={selectedMaterialType?.name} />
            </div>

            {/* View existing contract button */}
            <RestrictedComponent requiredScopes={['material:contract:view']} behavior="hide">
              <Button
                className="col-span-1 h-[40px]"
                type="button"
                variant="outline"
                disabled={!materialToEdit?.contractUrl}
                onClick={() => materialToEdit?.contractUrl && window.open(materialToEdit.contractUrl, '_blank')}
              >
                <Icon name="visibility" color="var(--primary)" size={22} />
                <span className="[font-family:'Figtree'] text-sm">Ver contrato</span>
              </Button>
            </RestrictedComponent>

            {/* Attach/edit contract button */}
            <RestrictedComponent requiredScopes={['material:contract:edit']} behavior="hide">
              <Button
                className="col-span-1 h-[40px]"
                type="button"
                style={{
                  backgroundColor: contract ? 'var(--primary)' : '#717171',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name="edit_document" color="#fff" size={22} />
                <span className="[font-family:'Figtree'] text-sm">
                  {contract ? 'Alterar contrato' : 'Anexar contrato'}
                </span>
              </Button>
            </RestrictedComponent>

            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                try {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Validate file type
                    const validTypes = [
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    ];
                    if (!validTypes.includes(file.type)) {
                      toast.error('Formato de arquivo inválido. Apenas PDF, DOC e DOCX são permitidos.');
                      e.target.value = ''; // Reset input
                      return;
                    }

                    // Validate file size (max 10MB)
                    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                    if (file.size > maxSize) {
                      toast.error('Arquivo muito grande. O tamanho máximo permitido é 10MB.');
                      e.target.value = ''; // Reset input
                      return;
                    }

                    setContract(file);
                    toast.success('Arquivo carregado com sucesso!');
                  }
                } catch (_error) {
                  toast.error('Erro ao carregar o arquivo. Por favor, tente novamente.');
                  e.target.value = ''; // Reset input on error
                }
              }}
            />

            <div className="col-span-2 flex w-full flex-col gap-1">
              <span className="text-xs font-medium text-[var(--gray-primary)]">
                Data de instalação <span className="text-muted-foreground">(opcional)</span>
              </span>
              <DatePicker
                mode="single"
                value={installedAt}
                onChange={(date) => {
                  if (date instanceof Date) {
                    setInstalledAt(date);
                  }
                }}
              />
            </div>
          </div>

          <div className="mt-4 flex w-full flex-row items-end gap-4">
            <div className="flex w-full flex-col gap-1">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca</FormLabel>
              <Input className="h-10" placeholder="Marca" disabled value={selectedMaterialType?.brand?.name} />
            </div>
            <div className="flex w-full flex-col gap-1">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número de série</FormLabel>
              <Input className="h-10" placeholder="Número de série" disabled value={code} />
            </div>
            <div className="flex w-full flex-col gap-1">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tags</FormLabel>
              <TagsDropdown
                selectedTags={form.getValues('selectedTags') ?? []}
                setSelectedTags={(tags) =>
                  form.setValue('selectedTags', tags, { shouldValidate: true, shouldDirty: true })
                }
              />
            </div>
          </div>

          <div className="mt-4 flex w-full flex-col gap-4 sm:flex-row">
            <FormField
              control={form.control}
              name="cnpj"
              rules={{ validate: (value) => checkMaterialEditInput('cnpj', value.uuid ?? value) }}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col gap-[3px]">
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CNPJ</FormLabel>
                  <FormControl>
                    <GenericSelect<FacilityDto>
                      disabled
                      onSelect={(facility) => {
                        field.onChange(facility.cnpj ?? facility);
                        setSelectedFacility(facility);
                        refetchCampaigns();
                      }}
                      value={field.value}
                      fetchItems={async (search) => {
                        setCnpjSearch(search);
                        if (!search) return [];
                        const result = await queryFacility.refetch();
                        return result.data?.facilities ?? [];
                      }}
                      renderItem={(facility) => (
                        <div>
                          <div className="text-sm font-medium">{facility.name}</div>
                          <div className="text-muted-foreground text-xs">{facility.cnpj}</div>
                        </div>
                      )}
                      getItemLabel={(facility) => facility.cnpj}
                      getItemKey={(facility) => facility.uuid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full flex-col gap-1">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Estabelecimento</FormLabel>
              <Input className="h-[36px]" placeholder="Estabelecimento" disabled value={selectedFacility?.name} />
            </div>
            <FormField
              control={form.control}
              name="exhibitionPoint"
              rules={{
                validate: (value) =>
                  checkMaterialEditInput('exhibitionPoint', !value || value?.trim() === '' ? undefined : value),
              }}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col gap-[3px]">
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Ponto de referência</FormLabel>
                  <FormControl>
                    <Input
                      className="h-[36px]"
                      placeholder="Ex: Corredor X"
                      disabled={!canEditMaterial}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        form.setValue(
                          'exhibitionPoint',
                          !inputValue || inputValue.trim() === '' ? undefined : inputValue,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          }
                        );
                      }}
                      onBlur={field.onBlur}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Device Manager Section - Only visible for master users */}
          {isMaster && (
            <div className="mt-4 flex w-full flex-col gap-4 sm:flex-row sm:items-end">
              <FormItem className="flex w-full flex-col gap-[3px]">
                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                  Gerenciador de dispositivos
                </FormLabel>
                <Select
                  value={deviceManagerType}
                  onValueChange={(value) => {
                    setDeviceManagerType(value as EDeviceManagerType);
                    form.setValue('deviceManagerType', value as EDeviceManagerType, {
                      shouldDirty: true,
                    });
                    if (value !== EDeviceManagerType.FIREBASE && value !== EDeviceManagerType.LED_PANEL) {
                      form.setValue('firebaseVerificationCode', undefined, {
                        shouldDirty: true,
                      });
                    }
                  }}
                  disabled
                >
                  <SelectTrigger className="h-[36px] w-full">
                    <SelectValue placeholder="Selecione o gerenciador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EDeviceManagerType.MAGICINFO}>Samsung</SelectItem>
                    <SelectItem value={EDeviceManagerType.FIREBASE}>Smart</SelectItem>
                    <SelectItem value={EDeviceManagerType.LED_PANEL}>Painel LED</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>

              <FormField
                control={form.control}
                name="firebaseVerificationCode"
                rules={{
                  validate: (value) => {
                    if (!isFirebaseFamily) return true;
                    if (!value || value.length !== 8) {
                      return 'Código de verificação deve ter 8 caracteres';
                    }
                    if (!/^[A-Z0-9]{8}$/.test(value)) {
                      return 'Código deve conter apenas letras e números';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col gap-[3px]">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                      Código de verificação
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-[36px]"
                        placeholder="Ex: A1B2C3D4"
                        disabled={!canEditMaterial || !isFirebaseFamily}
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const upperValue = e.target.value.toUpperCase().slice(0, 8);
                          field.onChange(upperValue);
                        }}
                        maxLength={8}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant="outline"
                className="h-[36px] shrink-0"
                disabled={!canEditMaterial || !isFirebaseFamily}
                onClick={() => {
                  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                  let code = '';
                  for (let i = 0; i < 8; i++) {
                    code += chars.charAt(Math.floor(Math.random() * chars.length));
                  }
                  form.setValue('firebaseVerificationCode', code, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              >
                <Icon name="autorenew" size={16} />
                <span className="ml-1">Gerar código</span>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
